import {
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useState,
} from "react";
import cx from "classnames";
import { Button, ButtonProps } from ".";
import styles from "./Button.module.scss";
import { useLayoutPropsUtil } from "../../hooks";

export type ButtonToggleState = {
  pressed: boolean;
  value?: string;
};

/**
 * Props for the ButtonToggle component
 * @extends Omit<ButtonProps, "appearance" | "loading">
 */
export type ButtonToggleProps = Omit<ButtonProps, "appearance" | "loading"> & {
  /**
   * The default pressed(selected) state of the toggle button.
   * @default false
   */
  defaultChecked?: boolean;

  /**
   * The controlled pressed(selected) state of the toggle button.
   */
  checked?: boolean;

  /**
   * The value of the toggle button.
   */
  value?: string;

  /**
   * Callback for when pressed(selected) changes.
   */
  onChange?: (
    e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
    state: ButtonToggleState,
  ) => void;
};

/**
 * ButtonToggle component for creating toggleable button states.
 *
 * Features:
 * - Supports both controlled and uncontrolled state management
 * - Toggle state with pressed/selected visual feedback
 * - Full accessibility support with aria-pressed attribute
 * - Callback support for state changes
 * - Supports layout utilities for positioning and spacing
 * - Inherits all button features except appearance and loading
 * - Flexible content with text and/or icons
 * - Keyboard interaction support
 * - Value association for form-like behavior
 *
 * @example
 * <ButtonToggle
 *   defaultChecked={false}
 *   onChange={(e, state) => console.log('Toggle state:', state.pressed)}
 * >
 *   Toggle Me
 * </ButtonToggle>
 */
export const ButtonToggle = forwardRef<HTMLButtonElement, ButtonToggleProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      value,
      defaultChecked = false,
      checked,
      className,
      onClick,
      onChange,
      onKeyDown,
      style,
      ...rest
    } = componentProps;
    const [isPressed, setIsPressed] = useState(checked ?? defaultChecked);

    const ButtonToggleClassNames = cx(className, styles["button-toggle"]);
    const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      if (typeof checked === "undefined") {
        setIsPressed(!isPressed);
        onChange?.(e, { pressed: !isPressed, value });
      }
      onClick?.(e);
    };

    useEffect(() => {
      if (typeof checked === "undefined") return;
      setIsPressed(checked);
    }, [checked]);

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <Button
        data-anv="button-toggle"
        aria-pressed={isPressed}
        onClick={onClickHandler}
        className={ButtonToggleClassNames}
        value={value}
        style={styleCombined}
        ref={ref}
        {...rest}
      />
    );
  },
);

ButtonToggle.displayName = "ButtonToggle";
